'use client';

import { useEffect, useRef, useState } from 'react';
import { VideoContent, VideoModal } from '@/app_components/Video/VideoModal';
import { useOverrideStnPlayerStyles } from '@/app_lib/hooks/useOverrideStnPlayerStyles';
import type { Video } from '@/app_lib/types/Video';

const VideoContentStn = ({
  target,
  videos,
  currentVideoIndex,
  onNextVideo,
  initialVideoId: initialVideoIdProp,
}: {
  target: string;
  videos: Video[];
  currentVideoIndex: number;
  onNextVideo: () => void;
  initialVideoId?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<VoltaxPlayer | null>(null);
  const onNextVideoRef = useRef(onNextVideo);

  // When exactly one player already exists on the page (e.g. the hero),
  // append a `dedupKey` to the Script src so Next.js creates a second
  // player instead of deduplicating. When two players already exist the
  // modal can reuse the second one, so no dedupKey is needed.
  const [{ stnVideoId, dedupKey }] = useState(() => {
    const preferredId = initialVideoIdProp ?? videos[0].id;

    if (typeof document === 'undefined') {
      return { dedupKey: undefined as string | undefined, stnVideoId: preferredId };
    }

    const playerCount = window.voltax?.getAllPlayersOnPage()?.length ?? 0;
    if (playerCount === 1) {
      return { dedupKey: target, stnVideoId: preferredId };
    }

    return { dedupKey: undefined as string | undefined, stnVideoId: preferredId };
  });
  onNextVideoRef.current = onNextVideo;

  // useEffect(() => {
  //   // Figure out how to change video when it ends
  // }, [currentVideoIndex, videos, videoType, onNextVideo]);

  // Use replacePlayerContent() when currentVideo changes
  useEffect(() => {
    const currentVideoId = videos[currentVideoIndex].id;
    if (!playerRef.current) {
      return;
    }

    if (playerRef.current) {
      playerRef.current.replacePlayerContent({ playerKey: currentVideoId });
    }
  }, [currentVideoIndex, videos]);

  useOverrideStnPlayerStyles(target);

  // Set up Voltax player listeners once (player is stable, never re-created)
  // biome-ignore lint/correctness/useExhaustiveDependencies: player setup references stable refs; currentVideoIndex triggers content replacement
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let isAdvancing = false;
    let isFirstVideoStart = true;
    let listenedVideoEl: HTMLVideoElement | null = null;

    const onTimeUpdate = () => {
      if (!listenedVideoEl) {
        return;
      }

      const secondsBeforeVideoFinishes = 0.7;
      const remaining = listenedVideoEl.duration - listenedVideoEl.currentTime;
      if (!isAdvancing && remaining <= secondsBeforeVideoFinishes) {
        isAdvancing = true;
        onNextVideoRef.current();
      }
    };

    const setupTimeUpdate = () => {
      const voltaxPlayer = container.querySelector('voltax-player');
      const videoEl = voltaxPlayer?.shadowRoot?.querySelector('video');

      if (!videoEl || videoEl === listenedVideoEl) {
        return;
      }

      if (listenedVideoEl) {
        listenedVideoEl.removeEventListener('timeupdate', onTimeUpdate);
      }

      listenedVideoEl = videoEl;
      videoEl.addEventListener('timeupdate', onTimeUpdate);
    };

    const onVideoStart = () => {
      isAdvancing = false;
      setupTimeUpdate();

      const voltaxPlayer = container.querySelector('voltax-player');
      const shadowRoot = voltaxPlayer?.shadowRoot;
      if (!shadowRoot) {
        return;
      }

      // Interact with video to avoid stay/next prompt
      const playBtn = shadowRoot.querySelector<HTMLButtonElement>('.voltax-mp-play-button');
      playBtn?.click();
      playBtn?.click();

      if (isFirstVideoStart) {
        isFirstVideoStart = false;
        const videoEl = shadowRoot.querySelector('video');
        const muteBtn = shadowRoot.querySelector<HTMLButtonElement>('.voltax-mp-mute-button');
        if (videoEl?.muted) {
          muteBtn?.click();
        }
      }
    };

    const registerListeners = (player: VoltaxPlayer) => {
      playerRef.current = player;
      player.replacePlayerContent({ playerKey: videos[currentVideoIndex].id });
      player.on('videoStart', onVideoStart);
    };

    const setupListeners = () => {
      const players = window.voltax?.getAllPlayersOnPage() ?? [];
      if (players.length > 1) {
        registerListeners(players[1]);
      }
    };

    if (window.voltax) {
      setupListeners();
    }

    const onPlayerLoaded = () => setupListeners();
    window.addEventListener('voltaxPlayerLoaded', onPlayerLoaded);

    return () => {
      window.removeEventListener('voltaxPlayerLoaded', onPlayerLoaded);
      if (listenedVideoEl) {
        listenedVideoEl.removeEventListener('timeupdate', onTimeUpdate);
      }
    };
  }, [currentVideoIndex]);

  const activeVideo = videos[currentVideoIndex];

  return (
    <VideoContent
      containerRef={containerRef}
      currentVideoIndex={currentVideoIndex}
      dedupKey={dedupKey}
      scrollContainerRef={scrollContainerRef}
      target={target}
      videoId={stnVideoId ?? activeVideo.id}
      videos={videos}
      videoType='stn'
    />
  );
};

// NOTE: The state is built into the component, not the content component
// This allows us to preserve the state when the modal is closed and reopened
export const VideoModalStn = ({ target, videos }: { target: string; videos: Video[] }) => {
  const [open, setOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const onNextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % (videos.length || 1));
  };

  const content = (
    <VideoContentStn
      currentVideoIndex={currentVideoIndex}
      onNextVideo={onNextVideo}
      target={target}
      videos={videos}
    />
  );

  // Mute the hero player when the modal opens so audio doesn't overlap
  useEffect(() => {
    if (!open) {
      return;
    }

    const heroContainer = document.querySelector('[class*="video-player-hero"]');
    const shadowRoot = heroContainer?.querySelector('voltax-player')?.shadowRoot;
    const videoEl = shadowRoot?.querySelector('video');
    const muteBtn = shadowRoot?.querySelector<HTMLButtonElement>('.voltax-mp-mute-button');

    if (videoEl && !videoEl.muted && muteBtn) {
      muteBtn.click();
    }
  }, [open]);

  return (
    <VideoModal
      content={content}
      currentVideoIndex={currentVideoIndex}
      open={open}
      setCurrentVideoIndex={setCurrentVideoIndex}
      setOpen={setOpen}
      target={target}
      videos={videos}
      videoType='stn'
    />
  );
};
